import React from 'react';
import { Box } from '@mui/material';
import NavBar from '../components/navbar';
import Footer from '../components/footer';
import PageHero from '../components/PageHero';
import SectionHeader from '../components/SectionHeader';
import CtaSection from '../components/CtaSection';
import ScrollReveal from '../components/animated/ScrollReveal';
import Text from '../components/Text';
import { colors, gradients } from '../theme/designTokens';
import { useHashScroll } from '../hooks/useHashScroll';

const values = [
  {
    title: 'Foresight',
    description: 'We anticipate uncertainty so organizations can act before disruption becomes destiny.',
  },
  {
    title: 'Integrity',
    description: 'Trust is the foundation of resilience advisory. We earn it through clarity and accountability.',
  },
  {
    title: 'Integration',
    description: 'Resilience is an architecture. Insurance, technology, governance, and continuity must work as one.',
  },
  {
    title: 'Excellence',
            description: 'Consulting-grade rigor. Outcomes that match the stakes. No commodity thinking.',
  },
];

const WhoWeAre = () => {
  useHashScroll();

  return (
  <Box sx={{ bgcolor: colors.white, overflow: 'hidden' }}>
    <NavBar />
    <PageHero
      variant="dark"
      eyebrow="Who We Are"
      title="Enterprise Resilience."
      titleHighlight="By Design."
      subtitle="ALEgION is a Business Resilience Company that uses insurance as one of its strategic tools—positioned as Enterprise Resilience Advisory & Engineering, not a transactional intermediary."
      badges={['AERA™', 'Resilience Engineering', 'Strategic Advisory']}
      minHeight={{ xs: 360, md: 480 }}
    />

    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: { xs: 8, md: 12 } }}>
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        <ScrollReveal>
          <SectionHeader
            eyebrow="Our Story"
            title="Born to engineer resilience."
            align="left"
            highlightWords={['resilience']}
            subtitle="For years, the industry sold risk transfer. Clients needed something larger: business continuity, operational resilience, risk intelligence, trust, and confidence."
          />
        </ScrollReveal>
        <ScrollReveal delay={0.1}>
          <Text variant="body" color="secondary" sx={{ mb: 3, display: 'block' }}>
            ALEgION was built from deep experience in enterprise risk management, cyber risk, healthcare resilience, AI-driven monitoring, and insurance advisory. That combination allows us to design protection architectures—where insurance is one layer inside a broader resilience ecosystem.
          </Text>
          <Text variant="h3" color="primary" sx={{ mb: 2 }}>
            We help organizations become more resilient.
          </Text>
          <Text variant="body" color="secondary" sx={{ display: 'block' }}>
            We design resilience architectures where insurance becomes one component of an integrated protection strategy. That is how we elevate from transactional intermediary to strategic advisor—and how clients associate ALEgION with a distinctive approach to organizational resilience.
          </Text>
        </ScrollReveal>
      </Box>
    </Box>

    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: { xs: 8, md: 10 }, background: gradients.sectionDark }}>
      <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
        <ScrollReveal>
          <SectionHeader
            eyebrow="Purpose & Mission"
            title="Where risk ends and resilience begins."
            light
            highlightWords={['resilience']}
          />
        </ScrollReveal>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 4,
          }}
        >
          <Box
            id="strategic-advisory"
            sx={{ p: 3, border: `1px solid ${colors.glassBorder}`, scrollMarginTop: 96 }}
          >
            <Text variant="label" sx={{ color: colors.ochre, mb: 2, display: 'block' }}>
              Purpose
            </Text>
            <Text variant="h3" color="white" sx={{ mb: 2 }}>
              Future-proof organizations.
            </Text>
            <Text variant="body" color="muted" sx={{ mb: 2, display: 'block' }}>
              To help enterprises anticipate uncertainty, absorb disruption, recover faster, and emerge stronger.
            </Text>
            <Text variant="bodySm" color="muted">
              Strategic Advisory means elevating from transactional placement to board-level resilience counsel—protection architecture, risk intelligence, and continuity designed as one system.
            </Text>
          </Box>
          <Box
            id="resilience-engineering"
            sx={{ p: 3, border: `1px solid ${colors.glassBorder}`, scrollMarginTop: 96 }}
          >
            <Text variant="label" sx={{ color: colors.ochre, mb: 2, display: 'block' }}>
              Mission
            </Text>
            <Text variant="h3" color="white" sx={{ mb: 2 }}>
              Resilience Engineering.
            </Text>
            <Text variant="body" color="muted" sx={{ mb: 2, display: 'block' }}>
              Deliver integrated resilience, risk intelligence, insurance architecture, and technology—through AERA™.
            </Text>
            <Text variant="bodySm" color="muted">
              We engineer resilience the way critical systems are engineered: with foresight, layered controls, measurable maturity, and activation pathways when disruption hits.
            </Text>
          </Box>
        </Box>
      </Box>
    </Box>

    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: { xs: 8, md: 12 }, background: colors.offWhite }}>
      <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
        <ScrollReveal>
          <SectionHeader eyebrow="Values" title="What we stand for." highlightWords={['stand']} />
        </ScrollReveal>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
          }}
        >
          {values.map((v) => (
            <Box key={v.title} sx={{ p: 3, background: colors.white, border: `1px solid ${colors.borderLight}` }}>
              <Text variant="h4" color="primary" sx={{ mb: 1 }}>
                {v.title}
              </Text>
              <Text variant="bodySm" color="secondary">
                {v.description}
              </Text>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    <CtaSection
      secondaryLabel="Meet Leadership"
      secondaryPath="/leadership"
    />
    <Footer />
  </Box>
  );
};

export default WhoWeAre;
